import { XMarkIcon } from "@heroicons/react/24/outline";
import { FC, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FeedbackStatus } from "../../interfaces/endpoints/ICustomerFeedback";
import { updateFeedbackStatus } from "../../services/CustomerFeedback.service";
import { STATUS_OPTIONS } from "../../utils/customerFeedback/labels";
import Button from "../ui/Button";
import IconBtn from "../ui/IconBtn";
import Modal from "../ui/Modal";
import SelectInput from "../ui/input/SelectInput";
import { SelectOption } from "../ui/input/Input.types";

interface UpdateStatusModalProps {
  show: boolean;
  feedbackId: number;
  currentStatus: FeedbackStatus;
  onClose: () => void;
  // Called with the new status after a successful PUT — the parent
  // updates its local list state without refetching.
  onUpdated: (newStatus: FeedbackStatus) => void;
}

const UpdateStatusModal: FC<UpdateStatusModalProps> = ({
  show,
  feedbackId,
  currentStatus,
  onClose,
  onUpdated
}) => {
  const [newStatus, setNewStatus] = useState<FeedbackStatus>(currentStatus);
  const [submitting, setSubmitting] = useState(false);

  const statusOptions = useMemo<SelectOption[]>(
    () => STATUS_OPTIONS.map((o) => ({ id: o.value, name: o.label })),
    []
  );

  const handleSubmit = () => {
    if (newStatus === currentStatus) {
      onClose();
      return;
    }
    setSubmitting(true);
    updateFeedbackStatus(feedbackId, { status: newStatus })
      .then(() => {
        toast.success("Statusi u përditësua.", { position: "top-right" });
        onUpdated(newStatus);
        onClose();
      })
      .catch(() => {
        toast.error("Statusi nuk u përditësua.", { position: "top-right" });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Modal
      hasHeader={false}
      position="center"
      show={show}
      onClose={onClose}
      calcHeight={false}
      modalWidth="420px"
      modalHeight="260px"
      buttonElement={
        <Button onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Duke ruajtur..." : "Ruaj"}
        </Button>
      }
    >
      <div className="flex items-center justify-between px-6 pt-5 pb-3">
        <h3 className="text-base font-semibold text-tableColor">
          Ndrysho statusin
        </h3>
        <IconBtn onClick={onClose}>
          <XMarkIcon className="w-4 h-4 text-neutral" strokeWidth={2} />
        </IconBtn>
      </div>
      <div className="px-6">
        <SelectInput
          label="Statusi"
          options={statusOptions}
          selectedId={newStatus}
          onOptionSelect={(opt) => {
            if (opt) setNewStatus(opt.id as FeedbackStatus);
          }}
        />
      </div>
    </Modal>
  );
};

export default UpdateStatusModal;
